// 일정 탭 페이지 — 예정/지난/숨긴 그룹과 필터 칩.
//
// 필터 상태는 컴포넌트 내부 useState로 관리(state.eventFilter는 탭 전환 후
// 복귀 시 마지막 선택 유지용으로만 mirror). 새 일정/케밥/FAB 액션은
// 콜백으로 받는다.

import { Fragment } from "preact";
import { useState } from "preact/hooks";
import { api } from "../api";
import { isAdmin, isMember } from "../auth";
import { state } from "../state";
import { EventRowCard } from "../ui/components/EventRowCard";
import { Icon } from "../ui/components/Icon";
import { kstTodayStr } from "../ui/format";
import { mountPreact } from "../ui/preact-mount";

export interface EventListMatch {
  (ev: any): boolean;
}

export interface EventFilter {
  key: string;
  label: string;
  match?: EventListMatch;
}

export const EVENT_FILTERS: EventFilter[] = [
  { key: "all", label: "전체" },
  { key: "regular", label: "정기 모임", match: (ev) => ev.event_type === "regular" },
  { key: "individual", label: "개인전", match: (ev) => ev.event_type === "special" && ev.mode !== "team" },
  { key: "team", label: "팀전", match: (ev) => ev.event_type === "special" && ev.mode === "team" },
];

export interface RenderEventsParams {
  view: HTMLElement;
  onNew: () => void;
  onKebab: (ev: any, anchor: HTMLElement) => void;
  renderFab: (onClick: () => void) => void;
}

interface EventsListViewProps {
  events: any[];
  onKebab: (ev: any, anchor: HTMLElement) => void;
}

function ArchivedSection({
  archived,
  onKebab,
}: {
  archived: any[];
  onKebab: (ev: any, anchor: HTMLElement) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div class="section">
      <div
        class="section__title section__title--toggle"
        onClick={() => setOpen(!open)}
      >
        <h3>숨긴 일정</h3>
        <span class="count">{archived.length}개</span>
        <span class={"section__chevron" + (open ? " section__chevron--open" : "")}>
          <Icon name="chevron" size={14} />
        </span>
      </div>
      <div hidden={!open}>
        {archived.slice(0, 50).map((ev) => (
          <EventRowCard
            key={ev.event_id}
            ev={ev}
            onKebab={(anchor) => onKebab(ev, anchor)}
          />
        ))}
      </div>
    </div>
  );
}

function PastSection({
  past,
  onKebab,
}: {
  past: any[];
  onKebab: (ev: any, anchor: HTMLElement) => void;
}) {
  const groups = new Map<string, any[]>();
  for (const ev of past.slice(0, 50)) {
    const key = (ev.event_date || "").slice(0, 7) || "기타";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(ev);
  }
  return (
    <div class="section">
      <div class="section__title">
        <h3>지난 일정</h3>
        <span class="count">{past.length}개</span>
      </div>
      {[...groups.entries()].map(([ymKey, list]) => {
        const label = ymKey === "기타"
          ? "기타"
          : `${Number(ymKey.slice(5, 7))}월 ${ymKey.slice(0, 4)}`;
        return (
          <Fragment key={ymKey}>
            <div class="month-label">{label}</div>
            {list.map((ev) => (
              <EventRowCard
                key={ev.event_id}
                ev={ev}
                onKebab={(anchor) => onKebab(ev, anchor)}
              />
            ))}
          </Fragment>
        );
      })}
    </div>
  );
}

function EventsListView({ events, onKebab }: EventsListViewProps) {
  const initialFilter = state.eventFilter || "all";
  if (!state.eventFilter) state.eventFilter = initialFilter;
  const [activeKey, setActiveKey] = useState<string>(initialFilter);

  const filter = EVENT_FILTERS.find((f) => f.key === activeKey) || EVENT_FILTERS[0];
  const apply = (list: any[]) => (filter.match ? list.filter(filter.match) : list);

  const todayStr = kstTodayStr();
  const upcoming = apply(
    events
      .filter((ev) =>
        ev.status === "in_progress" ||
        ((ev.status === "draft" || ev.status === "ready") && (ev.event_date || "") >= todayStr),
      )
      .sort((a, b) => (a.event_date || "").localeCompare(b.event_date || "")),
  );
  const past = apply(
    events
      .filter((ev) =>
        ev.status === "completed" ||
        ((ev.status === "draft" || ev.status === "ready") && (ev.event_date || "") < todayStr),
      )
      .sort((a, b) => (b.event_date || "").localeCompare(a.event_date || "")),
  );
  const archived = apply(
    events
      .filter((ev) => ev.status === "archived")
      .sort((a, b) => (b.event_date || "").localeCompare(a.event_date || "")),
  );

  const allEmpty = upcoming.length === 0 && past.length === 0 && archived.length === 0;

  return (
    <Fragment>
      <div class="filter-chips">
        {EVENT_FILTERS.map((f) => (
          <button
            type="button"
            key={f.key}
            class={"filter-chip" + (f.key === activeKey ? " filter-chip--active" : "")}
            onClick={() => {
              state.eventFilter = f.key;
              setActiveKey(f.key);
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {allEmpty && (
        <div class="dash-empty">
          <p class="dash-empty__title">
            {activeKey === "all" ? "아직 일정이 없어요" : "해당 조건의 일정이 없어요"}
          </p>
          <p class="dash-empty__sub">
            {isAdmin() || isMember() ? "＋ 버튼으로 새 일정을 시작해보세요" : "운영진/회원이 일정을 만들면 여기에 표시돼요"}
          </p>
        </div>
      )}

      {upcoming.length > 0 && (
        <div class="section">
          <div class="section__title">
            <h3>예정 · 진행 중</h3>
            <span class="count">{upcoming.length}개</span>
          </div>
          {upcoming.map((ev) => (
            <EventRowCard
              key={ev.event_id}
              ev={ev}
              onKebab={(anchor) => onKebab(ev, anchor)}
            />
          ))}
        </div>
      )}

      {past.length > 0 && <PastSection past={past} onKebab={onKebab} />}

      {archived.length > 0 && isAdmin() && (
        <ArchivedSection archived={archived} onKebab={onKebab} />
      )}
    </Fragment>
  );
}

export async function renderEventsList(params: RenderEventsParams): Promise<void> {
  const { view, onNew, onKebab, renderFab } = params;
  view.innerHTML = "";
  const loading = document.createElement("div");
  loading.className = "loading";
  loading.textContent = "불러오는 중…";
  view.append(loading);

  let events: any[];
  try {
    events = await api("/api/v1/events");
  } catch (e: any) {
    view.innerHTML = "";
    const err = document.createElement("div");
    err.className = "error-card";
    err.textContent = e.message;
    view.append(err);
    if (isAdmin() || isMember()) renderFab(onNew);
    return;
  }

  view.innerHTML = "";
  // mountPreact + view.append 패턴 사용 — render(X, view)는 view._children에
  // 트리를 박아서, 다른 페이지가 view.innerHTML=""만 한 뒤 다시 같은 컴포넌트를
  // render하면 stale _children과 diff 시도해서 빈 화면 버그 발생.
  view.append(mountPreact(<EventsListView events={events} onKebab={onKebab} />));

  if (isAdmin()) renderFab(onNew);
}
